import { useState, type ReactNode } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import LabelIcon from '@mui/icons-material/Label';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SyncIcon from '@mui/icons-material/Sync';
import PaymentIcon from '@mui/icons-material/Payment';
import SettingsIcon from '@mui/icons-material/Settings';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAuth } from '@/hooks/useAuth';
import MobileAppBanner from '@/components/common/MobileAppBanner';
import DemoBanner from '@/components/common/DemoBanner';
import logoSvg from '@/../logo.svg';

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  icon: React.ElementType;
  iconColor: string;
  path: string;
  matchPrefix?: string;
  tourId?: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: DashboardIcon, iconColor: '#0466C8', path: '/dashboard', tourId: 'sidebar-dashboard' },
  { label: 'Church Members', icon: PeopleIcon, iconColor: '#9C27B0', path: '/members', matchPrefix: '/members', tourId: 'sidebar-members' },
  { label: 'Tasks', icon: TaskAltIcon, iconColor: '#4CAF50', path: '/tasks', matchPrefix: '/tasks', tourId: 'sidebar-tasks' },
  { label: 'Goals', icon: TrackChangesIcon, iconColor: '#E91E63', path: '/goals', matchPrefix: '/goals' },
  { label: 'Care Reasons', icon: MedicalServicesIcon, iconColor: '#2196F3', path: '/care-reasons', matchPrefix: '/care-reasons', tourId: 'sidebar-care-reasons' },
  { label: 'Groups', icon: LabelIcon, iconColor: '#FF9800', path: '/groups', matchPrefix: '/groups' },
];

const adminChildren: (NavItem & { hideInDemo?: boolean })[] = [
  { label: 'User Management', icon: ManageAccountsIcon, iconColor: '#F44336', path: '/admin/users', matchPrefix: '/admin/users' },
  { label: 'Integrations', icon: SyncIcon, iconColor: '#757575', path: '/admin/integrations', matchPrefix: '/admin/integrations', hideInDemo: true },
  { label: 'Billing', icon: PaymentIcon, iconColor: '#4CAF50', path: '/admin/billing', matchPrefix: '/admin/billing', hideInDemo: true },
];

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function AppLayout({ children }: { children: ReactNode }) {
  const { user, name, role, isDemo, churchName, logout } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showBanner = !isDesktop && !bannerDismissed;
  const isAdmin = role === 'admin' || role === 'superadmin';
  const [adminOpen, setAdminOpen] = useState(() => pathname.startsWith('/admin'));

  const isSelected = (item: NavItem) => {
    if (item.matchPrefix) return pathname.startsWith(item.matchPrefix);
    return pathname === item.path;
  };

  const handleNav = (path: string) => {
    navigate({ to: path });
    if (!isDesktop) setMobileOpen(false);
  };

  const visibleItems = navItems;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Drawer Header */}
      <Box
        sx={{
          backgroundColor: '#0466C8',
          px: 2,
          pt: 3,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src={logoSvg}
          alt="Flock Care"
          sx={{
            width: 160,
            height: 'auto',
            filter: 'brightness(0) invert(1)',
          }}
        />
        {churchName && (
          <Typography data-tour="sidebar-church-name" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12, mt: 1.5, textAlign: 'center' }}>
            {churchName}
          </Typography>
        )}
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px solid rgba(255, 255, 255, 0.15)',
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
          }}
        >
          {name && (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12 }}>
              {name}
            </Typography>
          )}
          {user?.email && (
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Navigation Items */}
      <List data-tour="sidebar-nav" sx={{ flex: 1, px: 1, pt: 1 }}>
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const selected = isSelected(item);
          return (
            <ListItemButton
              key={item.path}
              data-tour={item.tourId}
              selected={selected}
              onClick={() => handleNav(item.path)}
              sx={{
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: hexToRgba(item.iconColor, 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    backgroundColor: hexToRgba(item.iconColor, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: item.iconColor }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: selected ? 600 : 400, fontSize: 14 }}
              />
            </ListItemButton>
          );
        })}

        {/* Admin section with nested children */}
        {isAdmin && (
          <>
            <ListItemButton
              onClick={() => setAdminOpen((prev) => !prev)}
              selected={pathname.startsWith('/admin')}
              sx={{
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: hexToRgba('#F44336', 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    backgroundColor: hexToRgba('#F44336', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AdminPanelSettingsIcon sx={{ fontSize: 20, color: '#F44336' }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Admin"
                primaryTypographyProps={{ fontWeight: pathname.startsWith('/admin') ? 600 : 400, fontSize: 14 }}
              />
              {adminOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {adminChildren.filter((child) => !(isDemo && child.hideInDemo)).map((child) => {
                  const ChildIcon = child.icon;
                  const childSelected = isSelected(child);
                  return (
                    <ListItemButton
                      key={child.path}
                      selected={childSelected}
                      onClick={() => handleNav(child.path)}
                      sx={{
                        pl: 3,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: hexToRgba(child.iconColor, 0.08),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 44 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: '10px',
                            backgroundColor: hexToRgba(child.iconColor, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ChildIcon sx={{ fontSize: 20, color: child.iconColor }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{ fontWeight: childSelected ? 600 : 400, fontSize: 14 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </>
        )}

        {/* Platform Admin (superadmin only) */}
        {role === 'superadmin' && (
          <ListItemButton
            selected={pathname.startsWith('/platform')}
            onClick={() => handleNav('/platform')}
            sx={{
              mb: 0.5,
              '&.Mui-selected': {
                backgroundColor: hexToRgba('#F44336', 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  backgroundColor: hexToRgba('#F44336', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RocketLaunchIcon sx={{ fontSize: 20, color: '#F44336' }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Platform Admin"
              primaryTypographyProps={{ fontWeight: pathname.startsWith('/platform') ? 600 : 400, fontSize: 14 }}
            />
          </ListItemButton>
        )}
      </List>

      {/* Bottom section: Settings + Sign Out */}
      <Box sx={{ px: 1, pb: 2 }}>
        <ListItemButton
          selected={pathname === '/settings'}
          onClick={() => handleNav('/settings')}
          sx={{ mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 44 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '10px',
                backgroundColor: 'rgba(117, 117, 117, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SettingsIcon sx={{ fontSize: 20, color: '#757575' }} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{ fontWeight: pathname === '/settings' ? 600 : 400, fontSize: 14 }}
          />
        </ListItemButton>

        <Divider sx={{ my: 1 }} />

        <ListItemButton
          onClick={logout}
          sx={{ mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 44 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '10px',
                backgroundColor: 'rgba(117, 117, 117, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LogoutIcon sx={{ fontSize: 20, color: '#757575' }} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{ fontWeight: 400, fontSize: 14 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile hamburger button — fixed top-left, only on mobile */}
      {!isDesktop && (
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{
            position: 'fixed',
            top: showBanner ? 64 : 8,
            left: 8,
            transition: 'top 0.2s ease',
            zIndex: (t) => t.zIndex.drawer + 2,
            color: '#0466C8',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { backgroundColor: '#FFFFFF' },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Drawer */}
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          backgroundColor: 'background.default',
          ml: isDesktop ? 0 : 0,
          width: isDesktop ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
        }}
      >
        {isDemo && <DemoBanner />}
        {showBanner && <MobileAppBanner onDismiss={() => setBannerDismissed(true)} />}
        {children}
      </Box>
    </Box>
  );
}
